import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import NoResultAnimation from "../../assets/animations/No Result Green theme.json";
import { Button } from "react-bootstrap";
import moment from "moment";
import Paginator from "../../components/Paginator";
import { useUser } from "../../context/UserContext";
import { useTranslation } from "react-i18next";

const CompanyNotification = () => {
  const {
    userType,
    notifications,
    loadingNotifications,
    fetchNotifications,
    clearNotifications,
    markNotificationAsRead,
    readAllNotifications,
  } = useUser();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [currentPage, setCurrentPage] = useState(1);
  const [clearing, setClearing] = useState(false);
  const [readingAll, setReadingAll] = useState(false);
  const [localNotifications, setLocalNotifications] = useState([]);

  const { notifications: notificationList, pagination } =
    notifications || { notifications: [], pagination: { total_pages: 1, per_page: 10, total_records: 0 } };

  useEffect(() => {
    if (userType) fetchPage(currentPage);
  }, [userType, currentPage]);

  useEffect(() => {
    // Keep local state in sync with context notifications
    setLocalNotifications(notificationList || []);
  }, [notificationList]);

  const fetchPage = async (page) => {
    try {
      await fetchNotifications(page);
    } catch (err) {
      toast.error("Failed to fetch notifications.");
    }
  };

  const handleClearAll = async () => {
    try {
      setClearing(true);
      await clearNotifications();
      setCurrentPage(1);
      fetchPage(1);
    } finally {
      setClearing(false);
    }
  };

  const handleReadAll = async () => {
    try {
      setReadingAll(true);
      await readAllNotifications();
    } finally {
      setReadingAll(false);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= pagination.total_pages) {
      setCurrentPage(page);
    }
  };

  const handleMarkAsRead = async (id, index) => {
    try {
      await markNotificationAsRead(id);
      // Update local state immediately to reflect the read background
      setLocalNotifications((prev) => {
        const updated = [...prev];
        updated[index].is_read = true;
        return updated;
      });
    } catch (err) {
      toast.error("Failed to mark notification as read");
    }
  };

  return (
  <div className="inter-font p-3" style={{ 
  overflowY: "auto", 
  overflowX: "hidden",
  direction: isRTL ? 'rtl' : 'ltr'
}}>
  <div className="inter-font d-flex justify-content-between align-items-center flex-wrap px-3 pb-3">
    <h2 className="inter-font" style={{ fontSize: "19px", textAlign: isRTL ? 'right' : 'left' }}>
      {isRTL ? "الإشعارات" : "Notifications"}
    </h2>
    {localNotifications?.length > 0 && (
      <div className="d-flex gap-2">
        <Button
          className="read-all-btn inter-font border bg-transparent hover:bg-rose-50 transition-colors duration-200"
          style={{ 
            color: "#522A30", 
            height: "44px", 
            padding: "0 16px", 
            borderRadius: "6px", 
            borderColor: "#522A30", 
            fontWeight: 500 
          }}
          disabled={readingAll}
          onClick={handleReadAll}
        >
          {readingAll ? (isRTL ? "جاري القراءة..." : "Reading...") : t("ui_read_all")}
        </Button>
        <Button
          className="clear-btn inter-font border bg-transparent hover:bg-rose-50 transition-colors duration-200"
          style={{ 
            color: "#522A30", 
            height: "44px", 
            padding: "0 16px", 
            borderRadius: "6px", 
            borderColor: "#522A30", 
            fontWeight: 500 
          }}
          disabled={clearing}
          onClick={handleClearAll}
        >
          {clearing ? (isRTL ? "جاري المسح..." : "Clearing...") : t("ui_clear_all")}
        </Button>
      </div>
    )}
  </div>

  <div className="bg-white rounded-4 shadow-sm p-3" style={{ direction: 'ltr' }}>
    {loadingNotifications ? (
      <div className="d-flex justify-content-center align-items-center py-5">
        <p className="mb-0 text-muted">
          {isRTL ? "جاري التحميل..." : "Loading…"}
        </p>
      </div>
    ) : localNotifications?.length === 0 ? (
      <div className="text-center my-4">
        <div style={{ maxWidth: 200, margin: "0 auto" }}>
          <Lottie animationData={NoResultAnimation} loop={true} />
        </div>
        <p style={{ textAlign: 'center', marginTop: '10px' }}>
          {isRTL ? "لا توجد إشعارات" : "No notifications"}
        </p>
      </div>
    ) : (
      localNotifications.map((item, idx) => (
        <div
          key={idx}
          className={`d-flex align-items-start gap-3 border-bottom py-3 px-3 rounded-3`}
          style={{
            cursor: "pointer",
            marginBottom: "2px",
            backgroundColor: item.is_read ? "#FFFFFF" : "#E9ECEF", // gray for unread
          }}
          onClick={() => !item.is_read && handleMarkAsRead(item._id, idx)}
        >
          <div className="flex-grow-1" style={{ textAlign: 'left' }}>
            <p
              className="mb-1"
              style={{
                fontSize: "14px",
                fontWeight: item.is_read ? 400 : 600,
              }}
            >
              {item.message || "New notification"}
            </p>
            <span style={{ fontSize: "12px", color: "#919191" }}>
              {moment(item.createdAt).fromNow()} • {moment(item.createdAt).format("hh:mm A")}
            </span>
          </div>
        </div>
      ))
    )}

    {pagination.total_pages > 1 && (
      <div className="mt-3 d-flex justify-content-center">
        <Paginator
          page={currentPage}
          rows={pagination.per_page}
          totalRecords={pagination.total_records}
          onPageChange={handlePageChange}
        />
      </div>
    )}
  </div>
</div>
  );
};

export default CompanyNotification;
